import { useFormContext } from 'react-hook-form'
import { IntegrationServiceId } from '../../../../../../common/integrations'
import { IntegrationSettings } from '../../schema'
import { useMemo } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { DetailedCRMAccountIntegrationMapping, Filter, FilterExpression } from '../../../../../../models/Account'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../store/hooks'
import { useGetDescribeQuery } from '../../../../../../store/integrations/integrations'
import { FormFieldDefinition, IntegrationObjectRelationship } from '../../hooks'
import { IntegrationMappingFilterBuilder } from './IntegrationMappingFilterBuilder'

type MappingKey = 'contact' | 'additionalObjects'

interface IntegrationMappingFilterSectionProps {
  serviceId: IntegrationServiceId
  mappingKey: MappingKey
  mappingObjectIndex?: number
  title: string
  description: string
  placeholderFilters?: FilterExpression[]
}

export const IntegrationMappingFilterSection: React.FC<IntegrationMappingFilterSectionProps> = ({
  serviceId,
  mappingKey,
  mappingObjectIndex,
  title,
  description,
  placeholderFilters = [],
}) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()

  const contactObject = watch('mappings.contact.objectName')

  const mappings = watch('mappings')
  const mappingValue = mappings?.[mappingKey as keyof typeof mappings]

  const currentMapping =
    mappingObjectIndex !== undefined && Array.isArray(mappingValue)
      ? (mappingValue[mappingObjectIndex] as DetailedCRMAccountIntegrationMapping)
      : (mappingValue as DetailedCRMAccountIntegrationMapping)

  const currentObjectName = useMemo(() => currentMapping?.objectName || null, [currentMapping])
  const accountId = useAppSelector(selectAccountId)
  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: currentObjectName! },
    },
    { skip: !currentObjectName || !accountId },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])
  const currentObjectRelationships: IntegrationObjectRelationship[] = useMemo(() => describe?.data?.describe?.relationships || [], [describe])

  const currentFilters = useMemo(() => currentMapping?.filters || [], [currentMapping])

  const handleFiltersChange = (newFilters: Filter[]) => {
    const updatedSpecificMapping = {
      ...currentMapping,
      filters: newFilters,
      sync: currentMapping.objectName === contactObject ? { ...(currentMapping.sync || {}), filters: newFilters } : currentMapping.sync,
    }

    const updatedMappingValue =
      mappingObjectIndex !== undefined && Array.isArray(mappingValue)
        ? mappingValue.map((item, index) => (index === mappingObjectIndex ? updatedSpecificMapping : item))
        : updatedSpecificMapping

    const updatedMappings = {
      ...mappings,
      [mappingKey]: updatedMappingValue,
    }

    setValue('mappings', updatedMappings, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4 className='mb-2'>{title}</h4>
          <p className='zocks-text-gray-600'>{description}</p>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col>
          <IntegrationMappingFilterBuilder
            serviceId={serviceId}
            value={currentFilters}
            onChange={handleFiltersChange}
            availableFormFieldDefinitions={currentObjectFormFieldDefinitions}
            availableRelationships={currentObjectRelationships}
            loading={isDescribeFetching}
            placeholders={placeholderFilters}
          />
        </Col>
      </Row>
    </Container>
  )
}
